import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Modal } from "../../components/ui";
import { SmallLoading } from "../../main/Loading";
import { getDesignPages } from "../../api/CanvaApi";
import CanvaDesignPage from "./CanvaDesignPage";
import { fromJS } from "immutable";

interface CanvaPagesModalProps {
  designId: string;
  designTitle?: string;
  onClose: () => void;
  onExportImage: (filename: string, file: File, meta?: { width?: number; height?: number }) => Promise<void>;
}

const CanvaPagesModal = ({ designId, designTitle, onClose, onExportImage }: CanvaPagesModalProps) => {
  const { t } = useTranslation();

  const { data: designPages, isLoading: isLoadingDesignPages } = useQuery({
    queryKey: ["canva-design-pages", designId],
    queryFn: async () => {
      const result = await getDesignPages(designId);
      return result;
    },
    enabled: !!designId,
    staleTime: 60 * 1000,
  });

  return (
    <Modal
      isOpen={!!designId}
      onClose={onClose}
      title={designTitle ? `${t("Export")} · ${designTitle}` : t("Select Page to Export")}
      className="w-[800px] max-w-[95vw]"
    >
      <div className="min-h-[300px] max-h-[70vh] overflow-y-auto p-1">
        {isLoadingDesignPages ? (
          <div className="w-full h-64 flex flex-col items-center justify-center gap-3">
            <SmallLoading />
            <div className="text-sm text-gray-500">{t("Loading pages...")}</div>
          </div>
        ) : designPages?.items?.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-sm text-gray-500">
            {t("No pages found in this design.")}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {designPages?.items?.map((item: any) => (
              <CanvaDesignPage
                key={item.index}
                designId={designId}
                designTitle={designTitle}
                item={fromJS(item)}
                onExportImage={onExportImage}
              />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CanvaPagesModal;
